<template>
  <div class="layui-table-box">
    <table ref="table" style="display: none;">
      <thead>
        <tr>
          <td>
            <div ref="headCheckbox">
              <el-checkbox
                @input="rowSelectedChangedAll()"
                :value="isSelectAll()"
              />
            </div>
          </td>
        </tr>
      </thead>
      <tbody ref="body">
        <tr
          v-for="(row, rowIndex) in data"
          :key="getRowKey(row, rowIndex)"
          :class="rowClass(row)"
          @click="handleRowClick(row, rowIndex)"
        >
          <td
            v-for="(col, colIndex) in layuiCols[0]"
            :data-field="col.field"
            :data-key="tableIndex + '-0-' + colIndex"
            :key="colIndex"
            :align="col.align"
            :data-width="col.width"
            :data-minwidth="col.minWidth"
            :class="col.labelClassName"
          >
            <div :class="cellClass(colIndex)">
              <template v-if="col.type === 'checkbox'">
                <el-checkbox
                  @input="rowSelectedChanged(rowIndex)"
                  :value="isSelect(rowIndex)"
                />
              </template>
              <template v-else-if="columns[colIndex].data.scopedSlots">
                <cell
                  :row="row"
                  :column="columns[colIndex]"
                  :rowIndex="rowIndex"
                  :colIndex="colIndex"
                ></cell>
              </template>
              <template v-else>
                {{ row[col.field] }}
              </template>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: "layui-table",
  props: {
    data: {
      type: Array,
      default: () => []
    },
    rowClassName: {
      type: [Function, String]
    },
    rowKey: {
      type: [Function, String]
    }
  },
  data() {
    return {
      layuiDone: false,
      tableIndex: 0,
      selection: {}
    };
  },
  computed: {
    columns: vm =>
      vm.$slots.default.filter(
        n => n.componentOptions.tag === "layui-table-column"
      ),
    layuiCols: vm => [
      vm.columns
        .map(col => col.data.attrs)
        .map(attrs => ({
          field: attrs.prop,
          title: attrs.label,
          align: attrs.align,
          width: isNaN(attrs.width) ? attrs.width : +attrs.width,
          minWidth: isNaN(attrs["min-width"])
            ? attrs["min-width"]
            : +attrs["min-width"],
          labelClassName: attrs["label-class-name"],
          type:
            { selection: "checkbox", index: "number" }[attrs.type] ?? "normal"
        }))
    ],
    cellClass: vm => col => [
      "layui-table-cell",
      `laytable-cell-${vm.tableIndex}-0-${col}`,
      { "laytable-cell-checkbox": vm.layuiCols[0][col].type === "checkbox" }
    ],
    selectedRows: vm => vm.data.filter((r, i) => vm.selection[i]),
    rowClass: vm => (row, rowIndex) =>
      "function" === typeof vm.rowClassName
        ? vm.rowClassName({ row, rowIndex })
        : vm.rowClassName,
    getRowKey: vm => (row, rowIndex) =>
      "function" === typeof vm.rowKey
        ? vm.rowKey(row)
        : vm.rowKey
        ? row[vm.rowKey]
        : rowIndex
  },
  watch: {
    data() {
      this.selection = {};
    }
  },
  async created() {
    this.loadTask = (async () => {
      await this.loadStyle();
      await this.loadScripts();
    })();
  },
  async mounted() {
    await this.loadTask;
    await this.layuiInit();
  },
  updated() {
    this.scrollUpdate();
  },
  methods: {
    async loadStyle() {
      await this.$loadStyle(process.env.BASE_URL + "layui/css/layui.east.css");
    },
    async loadScripts() {
      await this.$loadScript(process.env.BASE_URL + "jquery/jquery.js");
      await this.$loadScript(process.env.BASE_URL + "layui/layui.js");
    },
    async layuiInit() {
      let layui = window.layui;
      layui.use(["table", "laypage"], () => {
        this.table = layui.table.render({
          elem: this.$refs.table,
          cols: this.layuiCols,
          data: [{}],
          limit: 1,
          done: () => {
            this.$nextTick(() => {
              this.tableIndex = this.table.config.index;
              let table = this.$el.querySelector(".layui-table-body table");
              table.innerHTML = "";
              table.appendChild(this.$refs.body);
              if (this.layuiCols[0]?.[0]?.type === "checkbox") {
                let div = this.$el.querySelector(
                  "thead .laytable-cell-checkbox"
                );
                div.innerHTML = "";
                div.appendChild(this.$refs.headCheckbox);
              }
            });
          }
        });
      });
    },
    isSelect(index) {
      return Boolean(this.selection[index]);
    },
    isSelectAll() {
      for (let i = 0; i < this.data.length; i++) {
        if (!this.isSelect(i)) {
          return false;
        }
      }
      return this.data.length > 0;
    },
    rowSelectedChanged(index) {
      this.$set(this.selection, index, !this.isSelect(index));
      this.$emit("selection-change", this.selectedRows);
    },
    rowSelectedChangedAll() {
      let flag = !this.isSelectAll();
      for (let i = 0; i < this.data.length; i++) {
        this.$set(this.selection, i, flag);
      }
      this.$emit("selection-change", this.selectedRows);
    },
    scrollUpdate() {
      this.$nextTick(function() {
        this.table?.resize();
      });
    },
    handleRowClick(row) {
      this.$emit(`row-click`, row);
    }
  },
  components: {
    cell: {
      props: {
        row: {},
        column: Object,
        rowIndex: Number,
        colIndex: Number
      },
      render(h) {
        return h(
          "div",
          this.column.data.scopedSlots.default({
            row: this.row,
            $index: this.rowIndex
          })
        );
      }
    }
  }
};
</script>

<style lang="less" scoped>
.layui-table-box {
  height: 100%;
  position: relative;
  ::v-deep {
    .layui-table-view {
      height: 100%;
      .layui-table-box {
        height: 100%;
        display: flex;
        flex-direction: column;
        .layui-table-header {
          flex: none;
        }
        .layui-table-body {
          flex: auto;
        }
      }
    }
  }
}
</style>
